import { useCallback, useRef } from 'react'
import { NextSeo } from 'next-seo'

import { Article, Tag } from 'api'
import { StrapiPagination } from 'api/strapi'
import { ArticlesList } from 'components/article'
import { Pagination } from 'components/pagination'
import { Section } from 'components/section'
import { CTASection } from 'sections/common/cta-section/cta-section'
import { NewslettersSubscribeForm } from 'sections/articles/newsletters'
import { routes } from 'utils/routes'
import { SearchForm } from 'sections/articles'
import { WelcomeArticleSection } from 'sections/common'
import { useScrollTo } from 'utils/use-scroll-to'
import { useIsChangeRouting } from 'utils/use-page-router-event'
import { Heading } from 'components/heading'

import Box from './box.svg'
import classes from './articles.module.css'

export interface Props {
  pagination: StrapiPagination
  articles: Article[]
  featuredArticle?: Article | null
  tags: Tag[]
  initSort: string | null
  initTag: string | null
  initSearch: string | null
}

export const ArticlesListMain = (props: { data: Props }) => {
  const { pagination, articles, featuredArticle, tags, ...initFilters } = props.data

  const searchForm = useRef<null | HTMLFormElement>(null)
  const scrollTo = useScrollTo()
  const hasArticles = !!articles.length

  const onHashChangeStart = useCallback(
    (path: string) => {
      if (path === routes.articles) return //don't scroll when page without filters
      scrollTo(searchForm)
    },
    [scrollTo],
  )

  const isChangingRoute = useIsChangeRouting({
    onChangeComplete: onHashChangeStart,
  })

  return (
    <>
      <NextSeo title="Insights Resources" />

      {featuredArticle && <WelcomeArticleSection article={featuredArticle} withLinkToArticle />}
      <Section theme={featuredArticle ? 'outerGapSecondSection' : 'outerGap'}>
        <SearchForm tags={tags} {...initFilters} key="search-form" ref={searchForm} />

        {!hasArticles && (
          <div className={classes.emptyContainer}>
            <Box className={classes.emptyIcon} />
            <Heading theme="h5" className={classes.emptyTitle}>
              We came up empty
            </Heading>

            <p className={classes.emptyDescription}>
              We couldn’t find any articles matching your query. Check your spelling or try
              searching for a different term.
            </p>
          </div>
        )}
        <ArticlesList
          items={articles}
          tagLinkNextScroll={false}
          className={classes.articleList}
          data-refreshing={isChangingRoute}
        />
        {hasArticles && <Pagination {...pagination} />}
      </Section>

      <Section theme="hero" className={classes.subscribe}>
        <NewslettersSubscribeForm />
      </Section>

      <CTASection withDivider={false} />
    </>
  )
}
